import React, { useEffect, useState } from 'react'
import { Grid, FormControl, TextField, Checkbox, Typography, CircularProgress } from '@material-ui/core'
import AutocompleteNative, { createFilterOptions } from '@material-ui/lab/Autocomplete'
import { useUsuarioService } from 'hooks/useUsuarioService'
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank'
import CheckBoxIcon from '@material-ui/icons/CheckBox'
import moment from 'moment'
import { useCasoPermissoesService } from 'hooks/useCasoPermissoesService'
import { ModalConfirmacao } from 'components/ModalConfirmacao'
import useStyles from './styles'
import { CustomButton } from 'components/CustomButton'

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />
const checkedIcon = <CheckBoxIcon fontSize="small" />

const filterOptions = createFilterOptions({
  limit: 100,
})

export const ModalGerenciarAcesso = (props) => {
  const { open, onClose, idCaso, usuarios, selecionados } = props
  const classes = useStyles()
  const usuarioService = useUsuarioService()
  const casoPermissoesService = useCasoPermissoesService()
  const [usuariosSelecionados, setUsuariosSelecionados] = useState(selecionados || [])
  const [usuariosDropdown, setUsuariosDropdown] = useState(usuarios || [])
  const [carregandoUsuarios, setCarregandoUsuarios] = useState(false)
  const [usuariosComAcesso, setUsuariosComAcesso] = useState([])
  const [carregandoAcessos, setCarregandoAcessos] = useState(false)
  const [processando, setProcessando] = useState(false)

  useEffect(() => {
    setUsuariosSelecionados(selecionados || [])
  }, [selecionados])

  useEffect(() => {
    if (open && (!usuarios || usuarios.length === 0)) {
      usuarioService.listarTodosOsUsuarios(setCarregandoUsuarios).then((res) => {
        if (res) setUsuariosDropdown(res)
      })
    }
  }, [open, usuarios])

  useEffect(() => {
    let ativo = true

    if (!open || !idCaso) {
      setUsuariosComAcesso([])
      setCarregandoAcessos(false)
      return () => {
        ativo = false
      }
    }

    setUsuariosComAcesso([])
    setCarregandoAcessos(true)
    casoPermissoesService
      .listarUsuariosPermitidosDoCaso(idCaso)
      .then((res) => {
        if (ativo) setUsuariosComAcesso(res || [])
      })
      .finally(() => {
        if (ativo) setCarregandoAcessos(false)
      })

    return () => {
      ativo = false
    }
  }, [open, idCaso])

  const handleConfirmar = async () => {
    if (!idCaso || usuariosSelecionados.length === 0) {
      setProcessando(false)
      setUsuariosSelecionados([])
      onClose()
      return
    }
    setProcessando(true)

    try {
      const ids = usuariosSelecionados.map((u) => u.id)
      await casoPermissoesService.adicionarUsuariosPermitidosAoCaso(idCaso, ids)
    } catch (error) {
      console.log(error)
    } finally {
      setProcessando(false)
      setUsuariosSelecionados([])
      onClose()
    }
  }

  const handleRevogarAcesso = async (userId) => {
    if (!idCaso || !userId) return
    setProcessando(true)

    try {
      await casoPermissoesService.revogarAcessoAoCaso(idCaso, [userId])
      await casoPermissoesService.listarUsuariosPermitidosDoCaso(idCaso).then((res) => {
        setUsuariosComAcesso(res || [])
      })
    } catch (error) {
      console.log(error)
    } finally {
      setProcessando(false)
    }
  }

  return (
    <ModalConfirmacao aberto={open} onClose={onClose} onConfirm={handleConfirmar} processando={processando} titulo="Conceder Acesso" confirmarLabel="Confirmar" cancelarLabel="Cancelar" maxWidth="sm">
      <Grid container style={{ marginTop: '10px' }}>
        <Grid item md={12} sm={12} xs={12} className={classes.item}>
          <FormControl fullWidth>
            <AutocompleteNative
              multiple
              options={usuariosDropdown || []}
              value={usuariosSelecionados}
              disableCloseOnSelect
              filterOptions={filterOptions}
              loading={carregandoUsuarios}
              getOptionLabel={(option) => `${option.nome?.toUpperCase() || ''} - ${option.cargo ? option.cargo.nome?.toUpperCase() : ''}`}
              getOptionSelected={(option, value) => option.id === value.id}
              onChange={(event, newValue) => {
                setUsuariosSelecionados(newValue)
              }}
              renderOption={(option, { selected }) => (
                <React.Fragment>
                  <Checkbox icon={icon} checkedIcon={checkedIcon} style={{ marginRight: 8 }} checked={selected} />
                  {`${option.nome?.toUpperCase() || ''} - ${option.cargo ? option.cargo.nome?.toUpperCase() : ''}`}
                </React.Fragment>
              )}
              renderInput={(params) => <TextField {...params} variant="outlined" label="Selecione os usuários" InputLabelProps={{ shrink: true }} />}
            />
          </FormControl>
        </Grid>
        <Grid item md={12} sm={12} xs={12} className={classes.item}>
          <Typography className={classes.subtitulo}>Usuários com acesso concedido</Typography>

          {carregandoAcessos && (
            <div className={classes.carregandoAcessos}>
              <CircularProgress size={24} />
            </div>
          )}

          {!carregandoAcessos && usuariosComAcesso.length === 0 && <Typography className={classes.listaVazia}>Nenhum usuário com acesso.</Typography>}

          {!carregandoAcessos && usuariosComAcesso.length > 0 && (
            <div className={classes.listaUsuarios}>
              {usuariosComAcesso.map((acesso) => {
                const usuario = acesso.usuario || {}
                const usuarioId = acesso.usuario?.id
                const nomeCompleto = [usuario.nome, usuario.sobrenome].filter(Boolean).join(' ').toUpperCase()
                const cargo = usuario.cargo?.nome?.toUpperCase()
                const dataInsercao = acesso.createdAt && moment(acesso.createdAt).isValid() ? moment(acesso.createdAt).format('DD/MM/YYYY') : 'Não informada'

                return (
                  <div key={acesso.id} className={classes.usuarioComAcesso}>
                    <div className={classes.identificacaoUsuario} style={{ flex: 1 }}>
                      <Typography className={classes.nomeUsuario}>{nomeCompleto || 'USUÁRIO NÃO INFORMADO'}</Typography>
                      {cargo && <Typography className={classes.cargoUsuario}>{cargo}</Typography>}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexShrink: 0, marginLeft: '16px' }}>
                      <Typography className={classes.dataAcesso}>Inserido em: {dataInsercao}</Typography>
                      <CustomButton variant="outlined" colorType="error" disabled={processando || usuariosComAcesso.length <= 1} size="small" style={{ minHeight: '32px', padding: '4px 12px' }} onClick={() => handleRevogarAcesso(usuarioId)}>
                        Revogar
                      </CustomButton>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </Grid>
      </Grid>
    </ModalConfirmacao>
  )
}
